import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../context/UserContext";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import {
  FaEnvelope,
  FaUser,
  FaPaperPlane,
  FaHeadset,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import axios from "axios";

const Contact = () => {
  const { user, setUser, language, setLanguage, saveLanguageToBackend } =
      useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    try {
      if (user) {
        setIsLoggedIn(true);

        setFormData((prev) => ({
          ...prev,
          name: user.username || "",
          email: user.email || "",
        }));
      }
    } catch (error) {
      console.error("Failed to load user data:", error);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validare
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.message.trim()
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    if (!formData.email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/contact`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setIsSent(true);
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });

        // Resetăm mesajul de succes după 5 secunde
        setTimeout(() => {
          setIsSent(false);
        }, 5000);
      } else {
        setError(
          response.data.message || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      console.error("Contact form error:", err);
      setError(
        err.response?.data?.message ||
          "Failed to send message. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const infoCards = [
    {
      icon: <FaEnvelope className="text-green-400 text-2xl" />,
      title: "Email",
      content: "support@visionroyale.com",
      description: "For general inquiries and support",
    },
    {
      icon: <FaHeadset className="text-green-400 text-2xl" />,
      title: "Support Hours",
      content: "24/7",
      description: "We respond within 24 hours",
    },
    {
      icon: <FaClock className="text-green-400 text-2xl" />,
      title: "Response Time",
      content: "< 24 hours",
      description: "Average response time",
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-4">
              Contact Us
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Have questions or need assistance? We're here to help. Send us a
              message and we'll get back to you as soon as possible.
            </p>
          </motion.div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {infoCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-6 backdrop-blur-xl text-center"
              >
                <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.2)] pointer-events-none"></div>
                <div className="flex justify-center mb-4">{card.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {card.title}
                </h3>
                <p className="text-green-400 font-semibold mb-2">
                  {card.content}
                </p>
                <p className="text-gray-400 text-sm">{card.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-6 md:p-8 backdrop-blur-xl"
          >
            <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.2)] pointer-events-none"></div>

            <div className="flex items-center gap-3 mb-6">
              <FaPaperPlane className="text-green-400 text-2xl" />
              <h2 className="text-2xl font-bold text-green-300">
                Send us a Message
              </h2>
            </div>

            {/* Success Message */}
            {isSent && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-900/30 border border-green-500/50 rounded-lg flex items-center gap-3"
              >
                <FaCheckCircle className="text-green-400 text-xl" />
                <p className="text-green-400">
                  Message sent successfully! We'll get back to you soon.
                </p>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-lg"
              >
                <p className="text-red-400">{error}</p>
              </motion.div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-400 mb-2 text-sm font-medium">
                    Your Name *
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      readOnly={isLoggedIn}
                      className={`w-full p-3 pl-10 rounded-lg border transition ${
                        isLoggedIn
                          ? "bg-gray-800/40 text-gray-300 border-gray-700 cursor-not-allowed"
                          : "bg-gray-800/60 text-white border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400"
                      }`}
                      placeholder={isLoggedIn ? formData.name : "Your Name"}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm font-medium">
                    Email Address *
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      readOnly={isLoggedIn}
                      className={`w-full p-3 pl-10 rounded-lg border transition ${
                        isLoggedIn
                          ? "bg-gray-800/40 text-gray-300 border-gray-700 cursor-not-allowed"
                          : "bg-gray-800/60 text-white border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400"
                      }`}
                      placeholder={isLoggedIn ? formData.email : "your_email@example.com"}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm font-medium">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                  placeholder="How can we help you?"
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm font-medium">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition resize-none"
                  placeholder="Please describe your question or issue in detail..."
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-3 px-8 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.6)] hover:from-green-600 hover:to-emerald-700 transition-all duration-200 flex items-center gap-2 ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-700 text-center">
              <p className="text-gray-500 text-sm">
                By sending this message, you agree to our{" "}
                <a
                  href="#"
                  className="text-green-400 hover:text-green-300 transition"
                >
                  Privacy Policy
                </a>
                . We'll get back to you within 24 hours.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Contact;
